import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Edit2,
  ExternalLink,
  Plus,
  Power,
  PowerOff,
  Trash2,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StudioConnectionForm } from "./StudioConnectionForm";
import { VideoHubControl } from "./VideoHubControl";
import { AtemControl } from "./AtemControl";
import { CONNECTION_TYPE_LABELS, type ConnectionType } from "./connectionTypes";
import {
  useBridgeConnections,
  useConnectDevice,
  useDisconnectDevice,
  type ConnectionStatus,
} from "@/hooks/useBridgeConnections";
import {
  useCreateStudioConnection,
  useDeleteStudioConnection,
  useStudioConnections,
  useUpdateStudioConnection,
} from "@/hooks/useStudioConnections";
import type { Studio } from "@/hooks/useStudios";
import type {
  ConnectionConfig,
  ConnectionInsert,
  ConnectionUpdate,
} from "@production-hub/shared";

interface StudioConnectionsProps {
  studio: Studio;
}

interface StudioDevice extends ConnectionConfig {
  status: ConnectionStatus | null;
}

async function triggerBridgePull(bridgeUrl: string | null | undefined) {
  if (!bridgeUrl) return;
  try {
    await fetch(`${bridgeUrl}/api/sync/pull`, { method: "POST" });
  } catch (error) {
    console.error("Failed to trigger bridge device pull:", error);
  }
}

function DeviceStatusBadge({
  status,
  hasBridge,
}: {
  status: ConnectionStatus | null;
  hasBridge: boolean;
}) {
  if (!hasBridge) {
    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground">
        No bridge
      </Badge>
    );
  }

  switch (status) {
    case "connected":
      return (
        <Badge variant="default" className="gap-1 bg-green-600">
          <Wifi className="h-3 w-3" />
          Connected
        </Badge>
      );
    case "connecting":
      return (
        <Badge variant="secondary" className="gap-1">
          Connecting...
        </Badge>
      );
    case "error":
      return (
        <Badge variant="destructive" className="gap-1">
          <WifiOff className="h-3 w-3" />
          Error
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <WifiOff className="h-3 w-3" />
          Disconnected
        </Badge>
      );
  }
}

export const StudioConnections = ({ studio }: StudioConnectionsProps) => {
  const bridgeUrl = studio.bridge_url;
  const hasBridge = !!bridgeUrl;
  const { data: savedConnections = [], isLoading } = useStudioConnections(
    studio.id,
  );
  const { data: bridgeConnections = [] } = useBridgeConnections(bridgeUrl);
  const createConnection = useCreateStudioConnection();
  const updateConnection = useUpdateStudioConnection();
  const deleteConnection = useDeleteStudioConnection();
  const connectDevice = useConnectDevice(bridgeUrl);
  const disconnectDevice = useDisconnectDevice(bridgeUrl);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteConnectionId, setDeleteConnectionId] = useState<string | null>(
    null,
  );
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [expandedDeviceId, setExpandedDeviceId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const statusMap = useMemo(
    () =>
      new Map<string, ConnectionStatus>(
        bridgeConnections.map((connection) => [
          connection.id,
          connection.status,
        ]),
      ),
    [bridgeConnections],
  );

  const devices = useMemo(
    () =>
      savedConnections.map((connection) => ({
        ...connection,
        status: bridgeUrl
          ? (statusMap.get(connection.id) ?? "disconnected")
          : null,
      })),
    [bridgeUrl, savedConnections, statusMap],
  );

  const filteredDevices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return devices;

    return devices.filter((device) => {
      const deviceType =
        CONNECTION_TYPE_LABELS[device.connection_type as ConnectionType] ??
        device.connection_type;
      return [
        device.name,
        device.host,
        device.model,
        device.management_url,
        deviceType,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query));
    });
  }, [devices, searchQuery]);

  const handleCreate = (connection: ConnectionInsert) => {
    createConnection.mutate(
      { studio_id: studio.id, connection },
      {
        onSuccess: () => {
          setIsCreateDialogOpen(false);
          void triggerBridgePull(bridgeUrl);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!deleteConnectionId) return;
    deleteConnection.mutate(
      { id: deleteConnectionId, studio_id: studio.id },
      {
        onSuccess: () => {
          if (expandedDeviceId === deleteConnectionId) {
            setExpandedDeviceId(null);
          }
          setDeleteConnectionId(null);
          void triggerBridgePull(bridgeUrl);
        },
      },
    );
  };

  const editingDevice =
    devices.find((device) => device.id === editingDeviceId) ?? null;

  const handleEdit = (connection: ConnectionUpdate) => {
    if (!editingDeviceId) return;
    updateConnection.mutate(
      {
        id: editingDeviceId,
        studio_id: studio.id,
        connection,
      },
      {
        onSuccess: () => {
          setEditingDeviceId(null);
          void triggerBridgePull(bridgeUrl);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-medium">Devices</h3>
          <p className="text-sm text-muted-foreground">
            Cloud-managed devices for this studio
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search devices"
            className="h-8 w-full sm:w-56"
          />
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={createConnection.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Device
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading devices...
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Management</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Network</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[280px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-muted-foreground"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Wifi className="h-8 w-8" />
                        <p>No devices configured</p>
                        <p className="text-sm">
                          Add a cloud-managed device for this studio
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredDevices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-muted-foreground"
                    >
                      No devices match your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDevices.map((device) => (
                    <DeviceRows
                      key={device.id}
                      device={device}
                      bridgeUrl={bridgeUrl}
                      hasBridge={hasBridge}
                      isExpanded={expandedDeviceId === device.id}
                      onToggleExpanded={() =>
                        setExpandedDeviceId((current) =>
                          current === device.id ? null : device.id,
                        )
                      }
                      onConnect={() => connectDevice.mutate(device.id)}
                      onDisconnect={() => disconnectDevice.mutate(device.id)}
                      onEdit={() => setEditingDeviceId(device.id)}
                      onDelete={() => setDeleteConnectionId(device.id)}
                      isConnecting={
                        connectDevice.isPending &&
                        connectDevice.variables === device.id
                      }
                      isDisconnecting={
                        disconnectDevice.isPending &&
                        disconnectDevice.variables === device.id
                      }
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!editingDeviceId}
        onOpenChange={(open) => !open && setEditingDeviceId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Device</DialogTitle>
          </DialogHeader>
          {editingDevice ? (
            <StudioConnectionForm
              key={editingDevice.id}
              onSubmit={handleEdit}
              onCancel={() => setEditingDeviceId(null)}
              isLoading={updateConnection.isPending}
              initialValues={{
                name: editingDevice.name,
                connection_type: editingDevice.connection_type,
                host: editingDevice.host,
                port: editingDevice.port ?? undefined,
                model: editingDevice.model ?? undefined,
                management_url: editingDevice.management_url ?? undefined,
                enabled: editingDevice.enabled,
              }}
              submitLabel="Save Changes"
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Device</DialogTitle>
          </DialogHeader>
          <StudioConnectionForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateDialogOpen(false)}
            isLoading={createConnection.isPending}
            submitLabel="Add Device"
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteConnectionId}
        onOpenChange={(open) => !open && setDeleteConnectionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Device?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the device from the cloud source of truth.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteConnection.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

interface DeviceRowsProps {
  device: StudioDevice;
  bridgeUrl: string | null | undefined;
  hasBridge: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onConnect: () => void;
  onDisconnect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

const DeviceRows = ({
  device,
  bridgeUrl,
  hasBridge,
  isExpanded,
  onToggleExpanded,
  onConnect,
  onDisconnect,
  onEdit,
  onDelete,
  isConnecting,
  isDisconnecting,
}: DeviceRowsProps) => {
  const hasControlPanel =
    device.connection_type === "videohub" || device.connection_type === "atem";
  const canShowControls =
    hasBridge &&
    hasControlPanel &&
    device.status === "connected" &&
    !!bridgeUrl;

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="font-medium">{device.name}</div>
          {device.model ? (
            <div className="text-sm text-muted-foreground">{device.model}</div>
          ) : null}
        </TableCell>
        <TableCell>
          {device.management_url ? (
            <a
              href={device.management_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-foreground/15 bg-foreground/[0.04] px-2.5 py-1 text-sm font-medium text-foreground transition-colors hover:bg-foreground/[0.08]"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell className="text-muted-foreground">
          {CONNECTION_TYPE_LABELS[device.connection_type as ConnectionType] ||
            device.connection_type}
        </TableCell>
        <TableCell className="font-mono text-sm">
          {device.host}
          {device.port ? `:${device.port}` : ""}
        </TableCell>
        <TableCell>
          <DeviceStatusBadge status={device.status} hasBridge={hasBridge} />
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-2">
            {hasBridge && device.status === "connected" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onDisconnect}
                disabled={isDisconnecting}
              >
                <PowerOff className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
            ) : null}
            {hasBridge && device.status !== "connected" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onConnect}
                disabled={isConnecting || device.status === "connecting"}
              >
                <Power className="h-4 w-4 mr-1" />
                Connect
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
            {canShowControls ? (
              <Button variant="outline" size="sm" onClick={onToggleExpanded}>
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Hide Controls
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Show Controls
                  </>
                )}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {canShowControls && isExpanded ? (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/20">
            <div className="py-2">
              {device.connection_type === "videohub" ? (
                <VideoHubControl
                  connectionId={device.id}
                  bridgeUrl={bridgeUrl}
                  connectionStatus={device.status ?? undefined}
                />
              ) : null}
              {device.connection_type === "atem" ? (
                <AtemControl
                  connectionId={device.id}
                  bridgeUrl={bridgeUrl}
                  connectionStatus={device.status ?? undefined}
                />
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};
